import { iscRequest } from "./isc-client";
import { STOCK_DEPOTS, type StockDepotId } from "./stock-depots";
import type { StockProduct } from "../pharmacy/types";

interface IscPaginated<T> {
  items: T[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

interface IscProduct {
  id: string;
  code: string;
  sku: string;
  barcode: string | null;
  name: string;
  status: string;
  metadata?: Record<string, unknown>;
  unit?: { symbol?: string; code?: string };
  category?: { code?: string; name?: string };
}

interface IscUnit {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

interface IscStore {
  id: string;
  code: string;
  name: string;
}

interface IscLedgerEntry {
  id: string;
  quantity: string | number;
  direction: string;
  createdAt: string;
  product?: { id: string; code: string; name: string };
  store?: { id: string; code: string; name: string };
  movement?: {
    id: string;
    type: string;
    reference: string | null;
    postedAt: string;
  };
}

export interface StockReceiptLedgerRow {
  id: string;
  movementId: string;
  productId: string;
  productCode: string;
  productName: string;
  storeName: string;
  quantity: number;
  reference: string;
  postedAt: string;
}

interface IscStockBalance {
  productId: string;
  quantityOnHand: string | number;
  quantityReserved?: string | number;
  product?: { id: string; name: string; code: string };
}

function readSellingPrice(metadata?: Record<string, unknown>): number | null {
  const value = metadata?.sellingPrice;
  return typeof value === "number" && value >= 0 ? value : null;
}

function readMinThreshold(metadata?: Record<string, unknown>): number {
  const value = metadata?.minThreshold;
  return typeof value === "number" && value >= 0 ? value : 0;
}

function mapProduct(p: IscProduct): StockProduct {
  return {
    id: p.id,
    code: p.code,
    sku: p.sku ?? p.code,
    label: p.name,
    barcode: p.barcode,
    unit: p.unit?.symbol ?? p.unit?.code ?? "u",
    status: p.status,
    sellingPrice: readSellingPrice(p.metadata),
    minThreshold: readMinThreshold(p.metadata),
    categoryCode: p.category?.code,
  };
}

let cachedStoreIds: Partial<Record<string, string>> | undefined;

async function resolveStoreId(storeCode: string): Promise<string | null> {
  if (!cachedStoreIds) {
    cachedStoreIds = {};
    try {
      const stores = await iscRequest<IscStore[]>("/stores");
      for (const store of stores) {
        cachedStoreIds[store.code] = store.id;
      }
    } catch {
      cachedStoreIds = {};
    }
  }
  return cachedStoreIds[storeCode] ?? null;
}

interface IscTenant {
  settings?: Array<{ key: string; value: unknown }>;
}

let cachedGlobalMinThreshold: number | undefined;

export const pharmacyInventoryApi = {
  async listProducts(): Promise<StockProduct[]> {
    const first = await iscRequest<IscPaginated<IscProduct>>("/products?limit=200");
    const items = [...first.items];
    if (first.meta.totalPages > 1) {
      const pages = await Promise.all(
        Array.from({ length: first.meta.totalPages - 1 }, (_, i) =>
          iscRequest<IscPaginated<IscProduct>>(`/products?page=${i + 2}&limit=200`),
        ),
      );
      for (const page of pages) items.push(...page.items);
    }
    return items.map(mapProduct);
  },

  async findByBarcode(barcode: string): Promise<StockProduct | null> {
    const trimmed = barcode.trim();
    if (!trimmed) return null;
    const result = await iscRequest<IscPaginated<IscProduct>>(
      `/products?search=${encodeURIComponent(trimmed)}&limit=20`,
    );
    const exact = result.items.find(
      (p) => p.barcode?.toLowerCase() === trimmed.toLowerCase(),
    );
    const product = exact ?? result.items[0] ?? null;
    return product ? mapProduct(product) : null;
  },

  async listUnits(): Promise<IscUnit[]> {
    return iscRequest<IscUnit[]>("/units");
  },

  async listStores(): Promise<IscStore[]> {
    return iscRequest<IscStore[]>("/stores");
  },

  async getStoreIdByCode(storeCode: string): Promise<string | null> {
    return resolveStoreId(storeCode);
  },

  async getPharmacyStoreId(): Promise<string | null> {
    return resolveStoreId(STOCK_DEPOTS.pharmacy.storeCode);
  },

  async getDepotStoreId(depot: StockDepotId): Promise<string | null> {
    return resolveStoreId(STOCK_DEPOTS[depot].storeCode);
  },

  async getDefaultStoreId(): Promise<string | null> {
    return this.getPharmacyStoreId();
  },

  async listProductsForDepot(depot: StockDepotId): Promise<StockProduct[]> {
    const { categoryCodes } = STOCK_DEPOTS[depot];
    const products = await this.listProducts();
    const byCategory = products.filter(
      (p) => p.categoryCode && categoryCodes.includes(p.categoryCode),
    );
    if (byCategory.length > 0) return byCategory;

    if (depot === "pharmacy") {
      const pharmaSkus = new Set(["MED-003", "MED-006", "MED-007", "SKU-MED-003", "SKU-MED-006", "SKU-MED-007"]);
      return products.filter(
        (p) =>
          p.categoryCode === "PHARMA" ||
          pharmaSkus.has(p.code) ||
          pharmaSkus.has(p.sku),
      );
    }

    return products.filter(
      (p) =>
        p.categoryCode === "CONS" ||
        p.categoryCode === "LAB" ||
        ["MED-001", "MED-002", "MED-004", "MED-005", "MED-008", "MED-009"].includes(p.code),
    );
  },

  async listPharmacyProducts(): Promise<StockProduct[]> {
    return this.listProductsForDepot("pharmacy");
  },

  async listStockBalancesForStore(storeId: string): Promise<
    Array<{
      productId: string;
      quantityOnHand: number;
      quantityReserved: number;
      quantityAvailable: number;
    }>
  > {
    const result = await iscRequest<IscPaginated<IscStockBalance>>(
      `/inventory/balance?storeId=${encodeURIComponent(storeId)}&limit=500`,
    );
    return result.items.map((row) => {
      const onHand = Number(row.quantityOnHand ?? 0);
      const reserved = Number(row.quantityReserved ?? 0);
      return {
        productId: row.product?.id ?? row.productId,
        quantityOnHand: onHand,
        quantityReserved: reserved,
        quantityAvailable: Math.max(0, onHand - reserved),
      };
    });
  },

  async createProduct(input: {
    code: string;
    sku: string;
    name: string;
    barcode?: string;
    unitId: string;
    sellingPrice?: number;
  }): Promise<StockProduct> {
    const product = await iscRequest<IscProduct>("/products", {
      method: "POST",
      body: input,
    });
    return mapProduct(product);
  },

  async updateSellingPrice(productId: string, sellingPrice: number): Promise<void> {
    await iscRequest(`/products/${productId}`, {
      method: "PUT",
      body: { sellingPrice },
    });
  },

  async upsertReservation(input: {
    productId: string;
    storeId: string;
    quantity: number;
    externalRef: string;
  }): Promise<void> {
    await iscRequest("/inventory/reservations", {
      method: "POST",
      body: input,
    });
  },

  async releaseReservation(externalRef: string): Promise<void> {
    await iscRequest("/inventory/reservations/release-by-ref", {
      method: "POST",
      body: { externalRef },
    });
  },

  async listPharmacyStockBalances(): Promise<
    Array<{
      productId: string;
      quantityOnHand: number;
      quantityReserved: number;
      quantityAvailable: number;
    }>
  > {
    const storeId = await this.getPharmacyStoreId();
    if (!storeId) return [];
    return this.listStockBalancesForStore(storeId);
  },

  async postReceipt(
    productId: string,
    storeId: string,
    quantity: number,
    reference = "PHARMACY-ENTRY",
  ): Promise<void> {
    await iscRequest("/inventory/receipts", {
      method: "POST",
      body: {
        reference,
        lines: [{ productId, quantity, destStoreId: storeId }],
      },
    });
  },

  async postIssue(
    productId: string,
    storeId: string,
    quantity: number,
    externalRef?: string,
  ): Promise<void> {
    await this.postIssueBatch({
      reference: "PHARMACY-SALE",
      lines: [{ productId, quantity, sourceStoreId: storeId, externalRef }],
    });
  },

  async postIssueBatch(input: {
    reference: string;
    lines: Array<{
      productId: string;
      quantity: number;
      sourceStoreId: string;
      externalRef?: string;
    }>;
  }): Promise<void> {
    if (input.lines.length === 0) return;
    await iscRequest("/inventory/issues", {
      method: "POST",
      body: {
        reference: input.reference,
        lines: input.lines.map((line) => ({
          productId: line.productId,
          quantity: line.quantity,
          sourceStoreId: line.sourceStoreId,
          ...(line.externalRef ? { externalRef: line.externalRef } : {}),
        })),
      },
    });
  },

  async listStockBalances(): Promise<
    Array<{
      productId: string;
      quantityOnHand: number;
      quantityReserved: number;
      quantityAvailable: number;
    }>
  > {
    const result = await iscRequest<IscPaginated<IscStockBalance>>(
      "/inventory/balance?limit=500",
    );
    return result.items.map((row) => {
      const onHand = Number(row.quantityOnHand ?? 0);
      const reserved = Number(row.quantityReserved ?? 0);
      return {
        productId: row.product?.id ?? row.productId,
        quantityOnHand: onHand,
        quantityReserved: reserved,
        quantityAvailable: Math.max(0, onHand - reserved),
      };
    });
  },

  async getGlobalMinThreshold(): Promise<number> {
    if (cachedGlobalMinThreshold !== undefined) return cachedGlobalMinThreshold;
    try {
      const tenant = await iscRequest<IscTenant>("/tenant");
      const row = tenant.settings?.find((s) => s.key === "inventory.global_min_threshold");
      const value = row?.value;
      if (typeof value === "number" && value >= 0) {
        cachedGlobalMinThreshold = value;
      } else if (typeof value === "string") {
        cachedGlobalMinThreshold = Number(value) || 0;
      } else {
        cachedGlobalMinThreshold = 0;
      }
    } catch {
      cachedGlobalMinThreshold = 0;
    }
    return cachedGlobalMinThreshold;
  },

  async listReceiptLedger(input?: {
    page?: number;
    limit?: number;
    storeId?: string;
  }): Promise<{ items: StockReceiptLedgerRow[]; meta: IscPaginated<unknown>["meta"] }> {
    const page = input?.page ?? 1;
    const limit = input?.limit ?? 50;
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (input?.storeId) params.set("storeId", input.storeId);

    const result = await iscRequest<IscPaginated<IscLedgerEntry>>(
      `/inventory/ledger?${params.toString()}`,
    );

    const items = result.items
      .filter((row) => row.movement?.type === "RECEIPT")
      .map((row) => ({
        id: row.id,
        movementId: row.movement?.id ?? "",
        productId: row.product?.id ?? "",
        productCode: row.product?.code ?? "—",
        productName: row.product?.name ?? "—",
        storeName: row.store?.name ?? row.store?.code ?? "—",
        quantity: Number(row.quantity ?? 0),
        reference: row.movement?.reference ?? "—",
        postedAt: row.movement?.postedAt ?? row.createdAt,
      }));

    return { items, meta: result.meta };
  },
};

export type { IscUnit, IscStore };
